import { memo, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextLink from 'LuxKit/TextLink'
import type { CalendarPickerMonthView } from 'selectors/offerPage/offerPageSelectors'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'
import { CalendarBurnContext } from '../CalendarBurnContext'
import { useCalendarBurnDiscountedPrice } from '../useCalendarBurnDiscountedPrice'

interface Props {
  duration: number
  flightsOnly?: boolean
  enableCalendarRecommendations: boolean
  month: CalendarPickerMonthView
  selected?: boolean
  hasPromo: boolean
  displayMemberPrice?: boolean
  userPoints?: number
  loyaltyProductType?: App.LoyaltyProductType
  isMultiRoom?: boolean
}

enum DisplayVariant {
  ShowContentPackageAvailable,
  ShowFlightsNotYetAvailable,
  ShowRecommendations,
  ShowFlightOnlyNoCache,
}

function OfferCalendarFlightsMonthTile(props: Props) {
  const {
    month,
    duration,
    flightsOnly,
    enableCalendarRecommendations,
    selected,
    hasPromo,
    displayMemberPrice,
    userPoints = 0,
    loyaltyProductType,
    isMultiRoom,
  } = props

  const intl = useIntl()
  const { enabled: displayPointsPrice } = useContext(CalendarBurnContext)
  const flightPriceAvailable = month.hotelTotal < month.packagedTotal

  const displayPrice = useCalendarBurnDiscountedPrice({
    total: month.packagedTotal,
    hotelPrice: month.hotelTotal,
    flightPrice: flightPriceAvailable ? Math.max(0, month.packagedTotal - month.hotelTotal) : 0,
    loyaltyProductType,
    userPoints,
    enabled: !!(displayPointsPrice && userPoints > 0),
  })

  const displayVariant = useMemo<DisplayVariant>(() => {
    if (month.hotelAvailable) {
      if (month.packagedAvailable) {
        if (flightsOnly && !month.flightCacheActive) {
          return DisplayVariant.ShowFlightOnlyNoCache
        }
        return DisplayVariant.ShowContentPackageAvailable
      } else if (!flightsOnly) {
        return DisplayVariant.ShowFlightsNotYetAvailable
      }
    }
    return DisplayVariant.ShowRecommendations
  }, [month.hotelAvailable, month.packagedAvailable, month.flightCacheActive, flightsOnly])

  const colour = useMemo(() => {
    if (hasPromo) {
      return 'highlight-primary-dark'
    } else if (displayMemberPrice && !selected) {
      return 'lux-plus'
    } else {
      return undefined
    }
  }, [hasPromo, displayMemberPrice, selected])

  const subTextColour = selected ? 'normal-contrast' : 'light-foreground-alt'

  const nightsWithFlightsFromLabel = intl.formatMessage({
    id: 'MWMCLYa-VdDeyIQNJcRo',
    defaultMessage: '{nights, plural, one {# night} other {# nights}} + flights from',
    description: 'Night count with flights and from label for flights calendar month tile',
  }, { nights: duration })
  const nightsFromLabel = intl.formatMessage({
    id: 'yt5z51796m8KrA4oUq9r',
    defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
    description: 'Night count with from label for flights calendar month tile',
  }, { nights: duration })

  const price = <BodyText colour={colour} as="span" variant="large" weight="bold">
    <FormatCurrency value={displayPrice} />
  </BodyText>

  return (<div>
    {displayVariant === DisplayVariant.ShowFlightOnlyNoCache && (
      <Caption variant="medium" colour={subTextColour}>
        {intl.formatMessage({
          id: 'LjCzrv8oB4pC75ewOQ2B',
          defaultMessage: '{nights, plural, one {# night} other {# nights}}',
          description: 'Night count for flights calendar month tile when no flight cache',
        }, { nights: duration })}
      </Caption>
    )}
    {displayVariant === DisplayVariant.ShowRecommendations && enableCalendarRecommendations && (
      <>
        <Caption variant="medium" colour={subTextColour}>
          {intl.formatMessage({
            id: 'WSUlE3osICEa5E6_NddL',
            defaultMessage: 'Sold out',
            description: 'Text shown when flights month tile is sold out in the flights section',
          })}
        </Caption>
        <TextLink size="small" weight="regular">
          {intl.formatMessage({
            id: 'd9YMsCYm32ZhMZJCJwtT',
            defaultMessage: 'View similar offers',
            description: 'Link text to view similar offers when flights month tile is sold out',
          })}
        </TextLink>
      </>
    )}
    {
      displayVariant === DisplayVariant.ShowContentPackageAvailable && (<>
        {!isMultiRoom && <Caption variant="medium" colour={subTextColour}>
          {flightPriceAvailable ? nightsWithFlightsFromLabel : nightsFromLabel}
        </Caption>}
        {isMultiRoom && <Caption variant="medium" colour={subTextColour}>
          <MobileMultipleRoomsDurationLabel shouldDisplayRoom shouldDisplayFlight />
        </Caption>}
        <Caption variant="medium" colour={colour ?? subTextColour}>
          {flightPriceAvailable && <FormattedMessage
            id="AxI4pwBbIn9Q_dWZdHCM"
            defaultMessage="{price} total*"
            description="Total price label with asterisk for flights calendar month tile"
            values={{ price }}
          />}
          {!flightPriceAvailable && <FormattedMessage
            id="8aKCMRFpbbInlDgX88zD"
            defaultMessage="{price} total* + flights"
            description="Total price label with asterisk and flights suffix for flights calendar month tile"
            values={{ price }}
          />}
        </Caption>
        {displayPointsPrice && userPoints > 0 && displayPrice !== month.packagedTotal && (
          <div><Caption variant="medium" colour="light-foreground-alt"><s><FormatCurrency value={month.packagedTotal} /></s></Caption></div>
        )}
      </>)
    }
    {
      displayVariant === DisplayVariant.ShowFlightsNotYetAvailable && (<>
        <Caption variant="medium" colour={subTextColour}>
          {intl.formatMessage({
            id: 'NM40McNj-QAIBDgTghPJ',
            defaultMessage: 'Flights not yet available',
            description: 'Message shown when flights are not yet available for a month in flights month tile',
          })}
        </Caption>
        <TextLink size="small" weight="regular">
          {intl.formatMessage({
            id: 'NybcRWR1jn5fYQ8iITpK',
            defaultMessage: 'View hotel only',
            description: 'Link text to view hotel-only pricing when flights are not yet available',
          })}
        </TextLink>
      </>)
    }
  </div>
  )
}

export default memo(OfferCalendarFlightsMonthTile)
